import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';

const RISK_COLOR   = { CRITICAL: '#dc2626', HIGH: '#f97316', MEDIUM: '#f59e0b', LOW: '#22c55e' };
const STATUS_COLOR = { COMPLETED: '#22c55e', GENERATING: '#3b82f6', FAILED: '#dc2626', PENDING: '#6b7280' };

function tok() { return localStorage.getItem('authToken'); }

// ── Phase Card (collapsible) ─────────────────────────────────────────────────
function PhaseCard({ phase, index }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(index === 0);
  const detectColor = { HIGH: '#dc2626', MEDIUM: '#f59e0b', LOW: '#22c55e' };

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', marginBottom: 8 }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
          background: '#f8fafc', cursor: 'pointer', userSelect: 'none' }}>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
          color: '#7c3aed', background: '#ede9fe', padding: '2px 8px', borderRadius: 4, whiteSpace: 'nowrap' }}>
          {t('PartnerRedTeam.phaseLabel', { number: phase.phaseNumber })}
        </span>
        <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{phase.phaseName}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#fff',
          background: detectColor[phase.detectionRisk] || '#6b7280', padding: '2px 8px', borderRadius: 4 }}>
          {t('PartnerRedTeam.detectionSuffix', { risk: phase.detectionRisk })}
        </span>
        <i className={`fas fa-chevron-${open ? 'up' : 'down'}`} style={{ color: '#94a3b8', fontSize: 12 }} />
      </div>

      {open && (
        <div style={{ padding: '14px 16px', background: '#fff', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {[['objective', t('PartnerRedTeam.objective'), phase.objective], ['expectedOutcome', t('PartnerRedTeam.expectedOutcome'), phase.expectedOutcome]].map(([key, label, text]) => (
              <div key={key}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
                  color: '#7c3aed', marginBottom: 4 }}>{label}</div>
                <p style={{ margin: 0, fontSize: 13, color: '#4b5563', lineHeight: 1.55 }}>{text}</p>
              </div>
            ))}
          </div>

          {phase.prerequisites && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
                color: '#374151', marginBottom: 4 }}>{t('PartnerRedTeam.prerequisites')}</div>
              <p style={{ margin: 0, fontSize: 13, color: '#4b5563', lineHeight: 1.55 }}>{phase.prerequisites}</p>
            </div>
          )}

          {phase.steps?.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
                color: '#374151', marginBottom: 6 }}>{t('PartnerRedTeam.attackSteps')}</div>
              <ol style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {phase.steps.map((s, i) => <li key={i} style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{s}</li>)}
              </ol>
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {[
              { label: 'MITRE ATT&CK', name: 'MITRE ATT&CK',          items: phase.techniquesUsed, bg: '#1e1b4b', color: '#c7d2fe', link: true },
              { label: 'Tools',        name: t('PartnerRedTeam.tools'),   items: phase.tools,          bg: '#f3f4f6', color: '#374151', border: '#d1d5db' },
              { label: 'CVEs',         name: 'CVEs',                   items: phase.exploitedCves,  bg: '#fef2f2', color: '#b91c1c', border: '#fecaca' },
              { label: 'Targets',      name: t('PartnerRedTeam.targets'), items: phase.targetAssets,   bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
            ].filter(g => g.items?.length).map(g => (
              <div key={g.label} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280' }}>{g.name}</span>
                {g.items.map(t => g.link
                  ? <a key={t} href={`https://attack.mitre.org/techniques/${t.replace('.','/')}`}
                       target="_blank" rel="noreferrer"
                       style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                         background: g.bg, color: g.color, textDecoration: 'none' }}>{t}</a>
                  : <span key={t} style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                      background: g.bg, color: g.color, border: g.border ? `1px solid ${g.border}` : 'none' }}>{t}</span>
                )}
              </div>
            ))}
          </div>

          {phase.mitigations?.length > 0 && (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#166534',
                marginBottom: 6 }}>{t('PartnerRedTeam.breakKillChain')}</div>
              <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 3 }}>
                {phase.mitigations.map((m, i) => <li key={i} style={{ fontSize: 13, color: '#166534', lineHeight: 1.5 }}>{m}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Plan Detail Modal ────────────────────────────────────────────────────────
function PlanModal({ planId, onClose }) {
  const { t } = useTranslation();
  const [plan, setPlan] = useState(null);
  const [load, setLoad] = useState(true);

  useEffect(() => {
    axiosInstance.get(`/red-team/plans/${planId}`, { headers: { Authorization: `Bearer ${tok()}` } })
      .then(r => setPlan(r.data))
      .finally(() => setLoad(false));
  }, [planId]);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      zIndex: 9000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '2rem 1rem', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: '100%',
        maxWidth: 860, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', margin: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px', borderBottom: '1px solid #e2e8f0', fontWeight: 700, color: '#0f172a' }}>
          <span>{t('PartnerRedTeam.attackPlanTitle', { id: planId })}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer',
            color: '#9ca3af', fontSize: 16, padding: '4px 8px', borderRadius: 4 }}>
            <i className="fas fa-times" />
          </button>
        </div>

        {load && (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>
            <i className="fas fa-spinner fa-spin" /> {t('PartnerRedTeam.loadingPlan')}
          </div>
        )}

        {plan && (
          <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 20,
            overflowY: 'auto', maxHeight: '80vh' }}>
            {/* Meta row */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
              <span style={{ background: RISK_COLOR[plan.riskRating] || '#6b7280', color: '#fff',
                fontWeight: 700, fontSize: 12, padding: '4px 12px', borderRadius: 6, letterSpacing: '0.05em' }}>
                {plan.riskRating}
              </span>
              {[
                ['fa-clock', plan.estimatedTta],
                ['fa-calendar', new Date(plan.createdAt).toLocaleDateString()],
              ].map(([icon, text]) => (
                <span key={icon} style={{ fontSize: 13, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 5 }}>
                  <i className={`fas ${icon}`} style={{ color: '#7c3aed', fontSize: 12 }} />{text}
                </span>
              ))}
            </div>

            {plan.threatActorProfile && (
              <div style={{ fontSize: 14, color: '#374151' }}>
                <strong>{t('PartnerRedTeam.threatActor')} </strong>{plan.threatActorProfile}
              </div>
            )}

            <div style={{ fontSize: 14, color: '#4b5563', lineHeight: 1.6, background: '#f8fafc',
              borderLeft: '3px solid #7c3aed', borderRadius: '0 6px 6px 0', padding: '12px 16px' }}>
              {plan.executiveSummary}
            </div>

            {plan.attackNarrative && (
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 8 }}>
                  <i className="fas fa-scroll" style={{ marginRight: 6, color: '#7c3aed' }} />{t('PartnerRedTeam.attackNarrative')}
                </div>
                <p style={{ margin: 0, fontSize: 13, color: '#4b5563', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                  {plan.attackNarrative}
                </p>
              </div>
            )}

            {plan.criticalFindings?.length > 0 && (
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 8 }}>
                  <i className="fas fa-crosshairs" style={{ marginRight: 6, color: '#dc2626' }} />
                  {t('PartnerRedTeam.criticalFindings')}
                </div>
                <ul style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {plan.criticalFindings.map((f, i) => (
                    <li key={i} style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{f}</li>
                  ))}
                </ul>
              </div>
            )}

            {plan.phases?.length > 0 && (
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 10 }}>
                  <i className="fas fa-sitemap" style={{ marginRight: 6, color: '#7c3aed' }} />
                  {t('PartnerRedTeam.attackPhases', { count: plan.phases.length })}
                </div>
                {plan.phases.map((ph, i) => <PhaseCard key={ph.id} phase={ph} index={i} />)}
              </div>
            )}

            {plan.recommendedRemediations?.length > 0 && (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '16px 20px' }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#166534', marginBottom: 10 }}>
                  <i className="fas fa-tools" style={{ marginRight: 6 }} />{t('PartnerRedTeam.recommendedRemediations')}
                </div>
                <ol style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {plan.recommendedRemediations.map((r, i) => (
                    <li key={i} style={{ fontSize: 13, color: '#166534', lineHeight: 1.5 }}>{r}</li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Credit Purchase Modal ────────────────────────────────────────────────────
function BuyCreditsModal({ packs, onClose }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [err, setErr]           = useState(null);

  const handleBuy = async () => {
    if (!selected) return;
    setLoading(true); setErr(null);
    try {
      const r = await axiosInstance.post('/red-team/partner/checkout',
        { quantity: selected },
        { headers: { Authorization: `Bearer ${tok()}` } });
      window.location.href = r.data.checkoutUrl;
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
      setLoading(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: '100%',
        maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>{t('PartnerRedTeam.buyRedTeamCredits')}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{t('PartnerRedTeam.creditEquals')}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer',
            color: '#9ca3af', fontSize: 16, padding: '4px 8px' }}>
            <i className="fas fa-times" />
          </button>
        </div>

        <div style={{ padding: '20px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
            {packs.map(pack => (
              <div key={pack.quantity}
                onClick={() => setSelected(pack.quantity)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '14px 16px', borderRadius: 10, cursor: 'pointer',
                  border: `2px solid ${selected === pack.quantity ? '#7c3aed' : '#e2e8f0'}`,
                  background: selected === pack.quantity ? '#f5f3ff' : '#fff',
                  transition: 'all 0.15s' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 20, height: 20, borderRadius: '50%',
                    border: `2px solid ${selected === pack.quantity ? '#7c3aed' : '#d1d5db'}`,
                    background: selected === pack.quantity ? '#7c3aed' : '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {selected === pack.quantity && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }} />}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>
                      {t('PartnerRedTeam.packTitle', { count: pack.quantity, label: pack.label })}
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                      {t('PartnerRedTeam.packDesc', { count: pack.quantity })}
                    </div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>{pack.priceLabel}</div>
                  {pack.saving && (
                    <div style={{ fontSize: 11, color: '#059669', fontWeight: 600 }}>{pack.saving}</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {err && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
              padding: '10px 14px', color: '#b91c1c', fontSize: 13, marginBottom: 14 }}>
              {err}
            </div>
          )}

          <button onClick={handleBuy} disabled={!selected || loading}
            style={{ width: '100%', height: 44, background: selected ? 'linear-gradient(135deg,#7c3aed,#4f46e5)' : '#e2e8f0',
              color: selected ? '#fff' : '#9ca3af', border: 'none', borderRadius: 10, fontSize: 15,
              fontWeight: 700, cursor: selected ? 'pointer' : 'not-allowed', display: 'flex',
              alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'all 0.15s' }}>
            {loading
              ? <><i className="fas fa-spinner fa-spin" /> {t('PartnerRedTeam.redirectingToStripe')}</>
              : <><i className="fas fa-credit-card" /> {t('PartnerRedTeam.proceedToCheckout')}</>}
          </button>

          <div style={{ textAlign: 'center', marginTop: 12, fontSize: 11, color: '#9ca3af' }}>
            <i className="fas fa-lock" style={{ marginRight: 4 }} />
            {t('PartnerRedTeam.securedByStripeNote')}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function PartnerRedTeam() {
  const { t } = useTranslation();
  const [info, setInfo]           = useState(null);
  const [domains, setDomains]     = useState([]);
  const [domainId, setDomainId]   = useState('');
  const [plans, setPlans]         = useState([]);
  const [plansLoad, setPlansLoad] = useState(false);
  const [generating, setGen]      = useState(false);
  const [genErr, setGenErr]       = useState(null);
  const [genResult, setGenResult] = useState(null);
  const [selectedPlanId, setSelPlan] = useState(null);
  const [showBuy, setShowBuy]     = useState(false);
  const [refresh, setRefresh]     = useState(0);
  const [findingsSummary, setFindingsSummary] = useState(null);
  const [summaryLoading,  setSummaryLoading]  = useState(false);

  // ── Advanced scan state ──────────────────────────────────────────────────────
  const ADV_MODULES = [
    { id: 'GIT_RECON',     icon: '📁', label: t('PartnerRedTeam.modGitReconLabel'),     desc: t('PartnerRedTeam.modGitReconDesc'),     targets: 'urls',       mitre: 'T1552.001', color: '#2563eb' },
    { id: 'ENV_SECRETS',   icon: '🔑', label: t('PartnerRedTeam.modEnvSecretsLabel'),   desc: t('PartnerRedTeam.modEnvSecretsDesc'),   targets: 'urls',       mitre: 'T1552.001', color: '#2563eb' },
    { id: 'DEFAULT_CREDS', icon: '🔓', label: t('PartnerRedTeam.modDefaultCredsLabel'), desc: t('PartnerRedTeam.modDefaultCredsDesc'), targets: 'urls',       mitre: 'T1078.001', color: '#2563eb' },
    { id: 'ACTUATOR',      icon: '⚙️', label: t('PartnerRedTeam.modActuatorLabel'),     desc: t('PartnerRedTeam.modActuatorDesc'),     targets: 'urls',       mitre: 'T1552.002', color: '#2563eb' },
    { id: 'CORS',          icon: '🌐', label: t('PartnerRedTeam.modCorsLabel'),         desc: t('PartnerRedTeam.modCorsDesc'),         targets: 'urls',       mitre: 'T1539',     color: '#2563eb' },
    { id: 'GHOSTCAT',      icon: '👻', label: t('PartnerRedTeam.modGhostcatLabel'),     desc: t('PartnerRedTeam.modGhostcatDesc'),     targets: 'ips',        mitre: 'T1190',     color: '#7c3aed' },
    { id: 'S3_WRITE',      icon: '🪣', label: t('PartnerRedTeam.modS3WriteLabel'),      desc: t('PartnerRedTeam.modS3WriteDesc'),      targets: 'bucketUrls', mitre: 'T1608.001', color: '#d97706' },
  ];
  const [advDomainId,      setAdvDomainId]      = useState('');
  const [advModules,       setAdvModules]        = useState(ADV_MODULES.map(m => m.id));
  const [advTargets,       setAdvTargets]        = useState(null);
  const [advTargetLoading, setAdvTargetLoading]  = useState(false);
  const [advRunning,       setAdvRunning]        = useState(false);
  const [advScanning,      setAdvScanning]       = useState(false);

  // Detect return from Stripe checkout
  const purchaseSuccess = new URLSearchParams(window.location.search).get('credits') === 'purchased';

  const loadInfo = useCallback(async () => {
    try {
      const r = await axiosInstance.get('/red-team/partner/info', { headers: { Authorization: `Bearer ${tok()}` } });
      setInfo(r.data);
    } catch { /* silent */ }
  }, []);

  const loadDomains = useCallback(async () => {
    try {
      const r = await axiosInstance.get('/asm/domains/names', { headers: { Authorization: `Bearer ${tok()}` } });
      setDomains(r.data || []);
    } catch { /* silent */ }
  }, []);

  const loadPlans = useCallback(async (dId) => {
    if (!dId) return;
    setPlansLoad(true);
    try {
      const r = await axiosInstance.get('/red-team/plans', {
        params: { domainId: dId }, headers: { Authorization: `Bearer ${tok()}` }
      });
      setPlans(r.data || []);
    } catch { /* silent */ }
    finally { setPlansLoad(false); }
  }, []);

  const loadFindingsSummary = useCallback(async (dId) => {
    if (!dId) { setFindingsSummary(null); return; }
    setSummaryLoading(true);
    try {
      const r = await axiosInstance.get('/red-team/findings-summary',
        { params: { domainId: dId }, headers: { Authorization: `Bearer ${tok()}` } });
      setFindingsSummary(r.data);
    } catch { setFindingsSummary(null); }
    finally { setSummaryLoading(false); }
  }, []);

  useEffect(() => { loadInfo(); loadDomains(); }, [loadInfo, loadDomains]);
  useEffect(() => { loadPlans(domainId); loadFindingsSummary(domainId); }, [domainId, refresh, loadPlans, loadFindingsSummary]);
  useEffect(() => { if (purchaseSuccess) { loadInfo(); } }, [purchaseSuccess, loadInfo]);

  const handleGenerate = async () => {
    setGen(true); setGenErr(null); setGenResult(null);
    try {
      const r = await axiosInstance.post('/red-team/generate',
        { domainId: Number(domainId) },
        { headers: { Authorization: `Bearer ${tok()}` } });
      setGenResult(r.data);
      setRefresh(n => n + 1);
      loadInfo(); // refresh credit balance
    } catch (e) {
      const status = e.response?.status;
      const msg    = e.response?.data?.error || e.message;
      setGenErr(status === 402 ? t('PartnerRedTeam.insufficientCredits', { msg }) : t('PartnerRedTeam.generationFailed', { msg }));
    } finally { setGen(false); }
  };

  const credits = info?.redTeamCredits ?? 0;

  const loadAdvTargets = useCallback(async (dId) => {
    if (!dId) { setAdvTargets(null); return; }
    setAdvTargetLoading(true);
    setAdvTargets(null);
    try {
      const r = await axiosInstance.get(`/asm/domains/${dId}/advanced-scan/targets`, { headers: { Authorization: `Bearer ${tok()}` } });
      setAdvTargets(r.data);
    } catch {
      setAdvTargets({ urls: [], ips: [], bucketUrls: [] });
    } finally {
      setAdvTargetLoading(false);
    }
  }, []);

  const handleStartAdvancedScan = async () => {
    if (!advDomainId || advModules.length === 0) return;
    setAdvRunning(true);
    const triggeredBy = localStorage.getItem('userName') || localStorage.getItem('username') || 'partner';
    try {
      await axiosInstance.post(`/asm/domains/${advDomainId}/advanced-scan`,
        { modules: advModules, triggeredBy },
        { headers: { Authorization: `Bearer ${tok()}` } });
      setAdvScanning(true);
      window.alert(t('PartnerRedTeam.advScanStarted'));
    } catch (e) {
      const status = e.response?.status;
      const msg    = e.response?.data?.error || e.response?.data || e.message;
      if (status === 402) {
        window.alert(t('PartnerRedTeam.advScanRequiresCredits', { msg }));
      } else {
        window.alert(t('PartnerRedTeam.advScanFailed', { msg }));
      }
    } finally {
      setAdvRunning(false);
    }
  };

  const toggleAdvModule = (id) => {
    setAdvModules(prev => prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, paddingBottom: 40 }}>

      {/* Purchase success banner */}
      {purchaseSuccess && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#f0fdf4',
          border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 16px',
          color: '#166534', fontSize: 14, fontWeight: 500 }}>
          <i className="fas fa-check-circle" style={{ fontSize: 18, color: '#22c55e' }} />
          {t('PartnerRedTeam.paymentSuccess')}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 16, padding: '20px 24px',
        background: 'linear-gradient(135deg,#1e1b4b 0%,#312e81 60%,#4c1d95 100%)',
        borderRadius: 14, color: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <i className="fas fa-skull-crossbones" style={{ fontSize: 32, opacity: 0.85 }} />
          <div>
            <h2 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 700, color: '#fff' }}>
              {t('PartnerRedTeam.headerTitle')}
            </h2>
            <p style={{ margin: 0, fontSize: 13, color: '#c4b5fd' }}>
              {t('PartnerRedTeam.headerSubtitle')}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <i className="fas fa-robot" style={{ color: '#c4b5fd' }} />
          <span style={{ fontSize: 12, fontWeight: 600, color: '#e0e7ff' }}>{t('PartnerRedTeam.poweredByClaudeOpus')}</span>
        </div>
      </div>

      {/* Credit balance + buy */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Balance card */}
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 24px' }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
            color: '#6b7280', marginBottom: 8 }}>{t('PartnerRedTeam.availableCredits')}</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 48, fontWeight: 800, color: credits > 0 ? '#7c3aed' : '#dc2626',
              lineHeight: 1 }}>{credits}</span>
            <span style={{ fontSize: 14, color: '#6b7280', fontWeight: 500 }}>
              {t('PartnerRedTeam.plansRemaining', { count: credits })}
            </span>
          </div>
          {credits === 0 && (
            <div style={{ fontSize: 13, color: '#dc2626', fontWeight: 500, marginBottom: 8 }}>
              <i className="fas fa-exclamation-triangle" style={{ marginRight: 5 }} />
              {t('PartnerRedTeam.purchaseCreditsPrompt')}
            </div>
          )}
          <div style={{ fontSize: 12, color: '#9ca3af' }}>
            {info?.firmName} · {t('PartnerRedTeam.creditConsumedNote')}
          </div>
        </div>

        {/* Buy card */}
        <div style={{ background: 'linear-gradient(135deg,#f5f3ff,#ede9fe)',
          border: '1px solid #ddd6fe', borderRadius: 12, padding: '20px 24px',
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1e1b4b', marginBottom: 6 }}>
              <i className="fas fa-coins" style={{ marginRight: 8, color: '#7c3aed' }} />{t('PartnerRedTeam.buyMoreCredits')}
            </div>
            <div style={{ fontSize: 13, color: '#5b21b6', marginBottom: 4 }}>{t('PartnerRedTeam.fromPricePerPlan')}</div>
            <div style={{ fontSize: 12, color: '#6d28d9' }}>{t('PartnerRedTeam.neverExpireSecured')}</div>
          </div>
          <button onClick={() => setShowBuy(true)}
            style={{ marginTop: 14, height: 40, background: '#7c3aed', color: '#fff', border: 'none',
              borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <i className="fas fa-credit-card" /> {t('PartnerRedTeam.buyCredits')}
          </button>
        </div>
      </div>

      {/* Domain selector + generate */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 24px' }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b', marginBottom: 14 }}>
          <i className="fas fa-crosshairs" style={{ marginRight: 8, color: '#7c3aed' }} />{t('PartnerRedTeam.generateAttackPlan')}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>
            {t('PartnerRedTeam.targetDomain')}
          </label>
          <select value={domainId}
            onChange={e => { setDomainId(e.target.value); setGenResult(null); setGenErr(null); }}
            style={{ border: '1px solid #d1d5db', borderRadius: 7, padding: '8px 12px',
              fontSize: 14, color: '#111827', background: '#fff', minWidth: 240, outline: 'none' }}>
            <option value="">{t('PartnerRedTeam.chooseDomain')}</option>
            {domains.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>

          {domainId && !generating && (
            <button onClick={handleGenerate} disabled={credits === 0}
              style={{ height: 40, padding: '0 20px',
                background: credits > 0 ? 'linear-gradient(135deg,#7c3aed,#4f46e5)' : '#e2e8f0',
                color: credits > 0 ? '#fff' : '#9ca3af', border: 'none', borderRadius: 8,
                fontSize: 14, fontWeight: 700, cursor: credits > 0 ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="fas fa-robot" />
              {credits > 0 ? t('PartnerRedTeam.generatePlanBtn') : t('PartnerRedTeam.noCreditsBtn')}
            </button>
          )}
        </div>

        {/* Intelligence preview — what Opus will analyze */}
        {domainId && !generating && (findingsSummary || summaryLoading) && (
          <div style={{ marginTop: 14, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 18px' }}>
            <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#475569', marginBottom: 10 }}>
              {t('PartnerRedTeam.intelligenceFeeding')}
            </div>
            {summaryLoading ? (
              <div style={{ color: '#64748b', fontSize: 13 }}><i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }} />{t('PartnerRedTeam.loadingFindings')}</div>
            ) : findingsSummary && (
              <>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                  {[
                    { key: 'critical', label: t('PartnerRedTeam.sumCritical'),   count: findingsSummary.criticalCount, color: '#dc2626', bg: '#fef2f2' },
                    { key: 'high',     label: t('PartnerRedTeam.sumHigh'),       count: findingsSummary.highCount,     color: '#ea580c', bg: '#fff7ed' },
                    { key: 'medium',   label: t('PartnerRedTeam.sumMedium'),     count: findingsSummary.mediumCount,   color: '#d97706', bg: '#fffbeb' },
                    { key: 'totalCves', label: t('PartnerRedTeam.sumTotalCves'), count: findingsSummary.totalCveCount, color: '#4c75af', bg: '#eff6ff' },
                    { key: 'subdomains', label: t('PartnerRedTeam.sumSubdomains'), count: findingsSummary.subdomainCount, color: '#7c3aed', bg: '#f5f3ff' },
                  ].map(s => (
                    <span key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 4, background: s.bg,
                      color: s.color, border: `1px solid ${s.color}30`, borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>
                      <span style={{ fontSize: 16, lineHeight: 1 }}>{s.count}</span> {s.label}
                    </span>
                  ))}
                </div>
                {findingsSummary.byModule && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {Object.entries(findingsSummary.byModule).filter(([,v]) => v > 0).map(([key, val]) => {
                      const labels = { gitExposure: { label: t('PartnerRedTeam.bmGitExposure'), color: '#7c3aed' }, envExposure: { label: t('PartnerRedTeam.bmEnvSecrets'), color: '#dc2626' },
                        actuator: { label: t('PartnerRedTeam.bmActuator'), color: '#ea580c' }, cloudBuckets: { label: t('PartnerRedTeam.bmCloudS3'), color: '#d97706' },
                        credentials: { label: t('PartnerRedTeam.bmDefaultCreds'), color: '#16a34a' }, cors: { label: 'CORS', color: '#0891b2' },
                        network: { label: t('PartnerRedTeam.bmNetwork'), color: '#0369a1' }, headers: { label: t('PartnerRedTeam.bmHeaders'), color: '#6b7280' } };
                      const m = labels[key] || { label: key, color: '#6b7280' };
                      return (
                        <span key={key} style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 5,
                          background: m.color + '12', color: m.color, border: `1px solid ${m.color}30` }}>
                          {m.label}: {val}
                        </span>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Generating */}
        {generating && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 16,
            background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 10, padding: '16px 20px' }}>
            <div style={{ width: 48, height: 48, background: '#7c3aed', borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className="fas fa-brain fa-spin" style={{ color: '#fff', fontSize: 18 }} />
            </div>
            <div>
              <div style={{ fontWeight: 700, color: '#1e1b4b', fontSize: 14 }}>
                {t('PartnerRedTeam.opusPlanning')}
              </div>
              <div style={{ fontSize: 13, color: '#5b21b6', marginTop: 2 }}>
                {t('PartnerRedTeam.analyzingFindings', { name: domains.find(d => String(d.id) === String(domainId))?.name })}
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {genErr && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14,
            background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
            padding: '12px 16px', color: '#b91c1c', fontSize: 13 }}>
            <i className="fas fa-exclamation-triangle" style={{ flexShrink: 0 }} />
            {genErr}
            {genErr.includes('credit') && (
              <button onClick={() => { setGenErr(null); setShowBuy(true); }}
                style={{ marginLeft: 'auto', background: '#7c3aed', color: '#fff', border: 'none',
                  borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                {t('PartnerRedTeam.buyCredits')}
              </button>
            )}
          </div>
        )}

        {/* Success */}
        {genResult && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14,
            background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8,
            padding: '12px 16px', color: '#166534', fontSize: 13 }}>
            <i className="fas fa-check-circle" style={{ fontSize: 16, color: '#22c55e', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <strong>{t('PartnerRedTeam.planGeneratedRisk')} </strong>
              <span style={{ fontWeight: 700, color: RISK_COLOR[genResult.riskRating] }}>
                {genResult.riskRating}
              </span>
              <span> · {t('PartnerRedTeam.planGeneratedMeta', { count: genResult.phaseCount, tta: genResult.estimatedTta })}</span>
            </div>
          </div>
        )}
      </div>

      {/* Plan history */}
      {domainId && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 20px', borderBottom: '1px solid #f1f5f9' }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#1e293b' }}>
              <i className="fas fa-history" style={{ marginRight: 8, color: '#7c3aed' }} />{t('PartnerRedTeam.attackPlanHistory')}
            </h3>
            <button onClick={() => loadPlans(domainId)}
              style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 6,
                padding: '4px 10px', fontSize: 12, color: '#6b7280', cursor: 'pointer' }}>
              <i className={`fas fa-sync ${plansLoad ? 'fa-spin' : ''}`} />
            </button>
          </div>

          {plansLoad && (
            <div style={{ padding: '20px', color: '#9ca3af', fontSize: 14 }}>
              <i className="fas fa-spinner fa-spin" /> {t('PartnerRedTeam.loading')}
            </div>
          )}
          {!plansLoad && plans.length === 0 && (
            <div style={{ padding: '20px', color: '#9ca3af', fontSize: 14 }}>
              {t('PartnerRedTeam.noPlansYet')}
            </div>
          )}
          {plans.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, color: '#374151' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  {[['num', '#'], ['status', t('PartnerRedTeam.thStatus')], ['risk', t('PartnerRedTeam.thRisk')], ['phases', t('PartnerRedTeam.thPhases')], ['estTta', t('PartnerRedTeam.thEstTta')], ['created', t('PartnerRedTeam.thCreated')], ['actions', '']].map(([hKey, h]) => (
                    <th key={hKey} style={{ padding: '8px 14px', textAlign: 'left', fontSize: 11,
                      fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6b7280',
                      whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {plans.map(p => (
                  <tr key={p.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 14px', color: '#7c3aed', fontWeight: 600 }}>#{p.id}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                        background: STATUS_COLOR[p.status] || '#6b7280', marginRight: 6, verticalAlign: 'middle' }} />
                      {p.status}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      {p.riskRating && (
                        <span style={{ background: RISK_COLOR[p.riskRating] || '#6b7280', color: '#fff',
                          fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4 }}>
                          {p.riskRating}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '10px 14px' }}>{p.phaseCount ?? '–'}</td>
                    <td style={{ padding: '10px 14px', maxWidth: 200, overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#6b7280' }}>
                      {p.estimatedTta || '–'}
                    </td>
                    <td style={{ padding: '10px 14px', color: '#6b7280' }}>
                      {p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '–'}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <button onClick={() => setSelPlan(p.id)}
                        style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 6,
                          padding: '4px 12px', fontSize: 12, color: '#374151', cursor: 'pointer',
                          display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <i className="fas fa-eye" /> {t('PartnerRedTeam.view')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Advanced ASM Scan ─────────────────────────────────────────────────── */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', background: 'linear-gradient(135deg, #1e3a5f 0%, #1e40af 100%)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 20 }}>🔬</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#fff' }}>{t('PartnerRedTeam.advAsmTitle')}</div>
            <div style={{ fontSize: 12, color: '#93c5fd', marginTop: 2 }}>
              {t('PartnerRedTeam.advAsmSubtitle')}
            </div>
          </div>
        </div>

        <div style={{ padding: '20px' }}>
          {/* Domain picker */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>{t('PartnerRedTeam.selectDomain')}</div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <select
                value={advDomainId}
                onChange={e => { setAdvDomainId(e.target.value); loadAdvTargets(e.target.value); }}
                style={{ flex: 1, padding: '9px 12px', border: '1.5px solid #cbd5e1', borderRadius: 8, fontSize: 14, color: '#0f172a', background: '#f8fafc' }}
              >
                <option value="">{t('PartnerRedTeam.chooseDomain')}</option>
                {domains.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              {advTargetLoading && <span style={{ color: '#64748b', fontSize: 13 }}>{t('PartnerRedTeam.loadingTargets')}</span>}
            </div>

            {/* Target preview */}
            {advTargets && (() => {
              const total = (advTargets.urls?.length || 0) + (advTargets.ips?.length || 0) + (advTargets.bucketUrls?.length || 0);
              if (total === 0) return (
                <div style={{ marginTop: 10, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 13, color: '#92400e' }}>
                  {t('PartnerRedTeam.noTargetsFound')}
                </div>
              );
              const tLabel = { urls: t('PartnerRedTeam.tWebUrl'), ips: t('PartnerRedTeam.tIp'), bucketUrls: t('PartnerRedTeam.tS3Bucket') };
              const tColor = { urls: '#2563eb', ips: '#7c3aed', bucketUrls: '#d97706' };
              return (
                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>{t('PartnerRedTeam.targetsFromLastScan', { total })}</div>
                  {['urls', 'ips', 'bucketUrls'].map(type => {
                    const list = advTargets[type] || [];
                    if (!list.length) return null;
                    return (
                      <div key={type}>
                        <span style={{ display: 'inline-block', background: tColor[type] + '18', color: tColor[type], border: `1px solid ${tColor[type]}40`, borderRadius: 5, padding: '1px 8px', fontSize: 11, fontWeight: 700, marginBottom: 5 }}>
                          {tLabel[type]} ({list.length})
                        </span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                          {list.map((t, i) => <code key={i} style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 5, padding: '1px 7px', fontSize: 11, color: '#334155' }}>{t}</code>)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          {/* Module selector */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{t('PartnerRedTeam.modules')}</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => setAdvModules(ADV_MODULES.map(m => m.id))} style={{ fontSize: 11, padding: '2px 9px', border: '1px solid #cbd5e1', borderRadius: 5, background: '#f8fafc', cursor: 'pointer', color: '#475569' }}>{t('PartnerRedTeam.all')}</button>
                <button onClick={() => setAdvModules([])} style={{ fontSize: 11, padding: '2px 9px', border: '1px solid #cbd5e1', borderRadius: 5, background: '#f8fafc', cursor: 'pointer', color: '#475569' }}>{t('PartnerRedTeam.none')}</button>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 8 }}>
              {ADV_MODULES.map(mod => {
                const active = advModules.includes(mod.id);
                const tlist  = advTargets?.[mod.targets] || [];
                return (
                  <label key={mod.id} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
                    padding: '10px 12px', borderRadius: 9,
                    border: `1.5px solid ${active ? mod.color + '50' : '#e2e8f0'}`,
                    background: active ? mod.color + '06' : '#fafafa',
                  }}>
                    <input type="checkbox" checked={active} onChange={() => toggleAdvModule(mod.id)} style={{ marginTop: 3, accentColor: mod.color, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 14 }}>{mod.icon}</span>
                        <span style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{mod.label}</span>
                        {advTargets && (
                          <span style={{ fontSize: 10, color: tlist.length ? mod.color : '#9ca3af', background: tlist.length ? mod.color + '12' : '#f1f5f9', border: `1px solid ${tlist.length ? mod.color + '30' : '#e5e7eb'}`, borderRadius: 4, padding: '1px 5px', fontWeight: 700 }}>
                            {tlist.length ? t('PartnerRedTeam.targetsCount', { count: tlist.length }) : t('PartnerRedTeam.noTargets')}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, lineHeight: 1.45 }}>{mod.desc}</div>
                      <span style={{ fontSize: 10, color: '#7c3aed', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 3, padding: '1px 5px', fontWeight: 600 }}>{mod.mitre}</span>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Launch button */}
          {(() => {
            const total = advTargets ? (advTargets.urls?.length || 0) + (advTargets.ips?.length || 0) + (advTargets.bucketUrls?.length || 0) : 0;
            const ready = advDomainId && advModules.length > 0 && total > 0 && !advRunning;
            return (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>
                  {t('PartnerRedTeam.modulesSelected', { count: advModules.length })}
                  {advDomainId && advTargets ? ` · ${t('PartnerRedTeam.targetsQueued', { count: total })}` : ''}
                </div>
                <button
                  disabled={!ready}
                  onClick={handleStartAdvancedScan}
                  style={{
                    padding: '10px 24px', borderRadius: 9, border: 'none', fontSize: 14, fontWeight: 700,
                    background: ready ? '#1e40af' : '#e2e8f0',
                    color: ready ? '#fff' : '#94a3b8',
                    cursor: ready ? 'pointer' : 'not-allowed',
                  }}
                >
                  {advRunning ? t('PartnerRedTeam.starting') : t('PartnerRedTeam.runAdvancedScan')}
                </button>
              </div>
            );
          })()}

          {advScanning && (
            <div style={{ marginTop: 12, padding: '10px 14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 13, color: '#1e40af' }}>
              {t('PartnerRedTeam.advScanRunning')}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showBuy && info?.creditPacks && (
        <BuyCreditsModal packs={info.creditPacks} onClose={() => setShowBuy(false)} />
      )}
      {selectedPlanId && (
        <PlanModal planId={selectedPlanId} onClose={() => setSelPlan(null)} />
      )}
    </div>
  );
}
